import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { isAxiosError } from 'axios';
import {
  AlertCircle,
  AlertTriangle,
  ArrowLeft,
  CheckCircle2,
  Download,
  PenTool,
  RotateCcw,
  ShieldAlert,
  Volume2,
  VolumeX,
} from 'lucide-react';
import { Button } from '@/app/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/app/components/ui/card';
import { Input } from '@/app/components/ui/input';
import { Label } from '@/app/components/ui/label';
import { Textarea } from '@/app/components/ui/textarea';
import { toast } from '@/app/components/ui/simple-toaster';
import { useAuth } from '@/contexts/auth-context';
import { useResolveSerials } from '@/hooks/use-assets';
import { useBatchInspect } from '@/hooks/use-inspections';
import type {
  BatchEntryMethod,
  ReceiveGearItem,
  ResolvedSerialLine,
} from '@/lib/types';
import {
  BATCH_CHUNK_SIZE,
  downloadCsv,
  runInChunks,
  slugifyFilename,
  todayInputValue,
} from '@/app/pages/assets/batch-entry/batch-utils';
import type { BatchProgress, BatchSortKey } from '@/app/pages/assets/batch-entry/batch-utils';
import { BatchLineList } from '@/app/pages/assets/batch-entry/batch-line-list';
import type { BatchLine } from '@/app/pages/assets/batch-entry/batch-line-list';
import { batchInspectSignBlockReason, buildBatchInspectCsv, inspectOutcome } from './batch-inspect-utils';
import type { InspectLine } from './batch-inspect-utils';
import { SerialEntryPanel } from '@/app/pages/assets/batch-entry/serial-entry-panel';
import { useScanFeedback } from '@/app/pages/assets/batch-entry/use-scan-feedback';

const RESOLVE_CHUNK_SIZE = 200;

function defaultBatchName(): string {
  return `Batch inspect – ${todayInputValue()}`;
}

function errorMessage(error: unknown): string {
  if (isAxiosError<{ error?: string; detail?: string; pin?: string; signer_name?: string }>(error)) {
    const data = error.response?.data;
    return data?.error ?? data?.detail ?? data?.pin ?? data?.signer_name ?? error.message;
  }
  if (error instanceof Error) return error.message;
  return 'The request failed. These inspections were not confirmed as saved.';
}

export function BatchInspectPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const resolveSerials = useResolveSerials();
  const batchInspect = useBatchInspect();
  const { feedbackClasses, playFeedback, soundEnabled, toggleSound } = useScanFeedback();

  const singleInputRef = useRef<HTMLInputElement>(null);
  const lineIdRef = useRef(0);

  const [batchId, setBatchId] = useState(() => crypto.randomUUID());
  const [batchName, setBatchName] = useState(defaultBatchName);
  const [inspectionDate, setInspectionDate] = useState(todayInputValue);
  const [notes, setNotes] = useState('');
  const [signerName, setSignerName] = useState('');
  const [pin, setPin] = useState('');

  const [lines, setLines] = useState<InspectLine[]>([]);
  const [sortKey, setSortKey] = useState<BatchSortKey>('entered');
  const [groupByCategoryEnabled, setGroupByCategoryEnabled] = useState(true);
  const [isSigning, setIsSigning] = useState(false);
  const [progress, setProgress] = useState<BatchProgress>({ processed: 0, total: 0 });
  const [isFinished, setIsFinished] = useState(false);
  const [signAttempted, setSignAttempted] = useState(false);
  const [signServerError, setSignServerError] = useState<string | null>(null);

  const detailsReady = Boolean(batchName.trim() && inspectionDate);
  const requiresPin = Boolean(user?.has_signing_pin);

  useEffect(() => {
    if (detailsReady && !isFinished) singleInputRef.current?.focus();
  }, [detailsReady, isFinished]);

  const updateLine = (lineId: number, patch: Partial<InspectLine>) => {
    setLines((current) => current.map((line) => (line.id === lineId ? { ...line, ...patch } : line)));
  };

  const resolveLines = useCallback(async (pendingLines: InspectLine[]): Promise<ResolvedSerialLine[]> => {
    const responses = await runInChunks(pendingLines, RESOLVE_CHUNK_SIZE, async (chunk) => {
      const response = await resolveSerials.mutateAsync({
        serials: chunk.map((line) => line.serial),
        purpose: 'inspect',
      });
      setLines((current) => current.map((line) => {
        const index = chunk.findIndex((pending) => pending.id === line.id);
        if (index === -1) return line;
        return { ...line, resolved: response.results[index] ?? line.resolved };
      }));
      return response;
    }, {
      onChunkError: (error, chunk) => {
        const message = errorMessage(error);
        setLines((current) => current.map((line) => (
          chunk.some((pending) => pending.id === line.id)
            ? {
                ...line,
                resolved: {
                  serial: line.serial,
                  status: 'not_found',
                  message: `Lookup failed: ${message}`,
                  warnings: [],
                  asset: null,
                },
              }
            : line
        )));
      },
    });
    return responses.flatMap((response) => response?.results ?? []);
  }, [resolveSerials]);

  const addSerials = useCallback(async (serials: string[], entryMethod: BatchEntryMethod) => {
    if (!detailsReady) {
      toast.error('Enter the batch name and inspection date first.');
      return [];
    }
    const existing = new Set(lines.map((line) => line.serial.toLocaleLowerCase()));
    const newLines: InspectLine[] = [];
    const duplicateLines: InspectLine[] = [];
    for (const raw of serials) {
      const serial = raw.trim();
      if (!serial) continue;
      const key = serial.toLocaleLowerCase();
      const line: InspectLine = {
        id: ++lineIdRef.current,
        serial,
        entryMethod,
        resolved: null,
        result: 'pass',
        failNotes: '',
      };
      if (existing.has(key)) {
        duplicateLines.push({
          ...line,
          resolved: { serial, status: 'duplicate', message: 'Already in this batch', warnings: [], asset: null },
        });
        continue;
      }
      existing.add(key);
      newLines.push(line);
    }
    setIsFinished(false);
    setLines((current) => [...current, ...newLines, ...duplicateLines]);
    if (newLines.length === 0) {
      playFeedback('duplicate');
      return [];
    }
    return resolveLines(newLines);
  }, [detailsReady, lines, playFeedback, resolveLines]);

  const handleSingle = useCallback(async (serial: string) => {
    const [resolved] = await addSerials([serial], 'single');
    // Feedback for a single scan comes from the resolved status.
    if (resolved) {
      playFeedback(
        resolved.status === 'ready'
          ? 'success'
          : resolved.status === 'duplicate'
            ? 'duplicate'
            : 'error'
      );
    }
    singleInputRef.current?.focus();
  }, [addSerials, playFeedback]);

  const handleBatch = useCallback(async (items: ReceiveGearItem[], method: 'paste' | 'range') => {
    await addSerials(items.map((item) => item.serial), method);
    toast.success(`${items.length} serial${items.length === 1 ? '' : 's'} added — mark any failures, then sign once.`);
  }, [addSerials]);

  const pendingLines = lines.filter((line) => !line.outcome);
  const readyLines = pendingLines.filter((line) => line.resolved?.status === 'ready' && line.resolved.asset);
  const unmatchedLines = pendingLines.filter((line) => line.resolved && line.resolved.status !== 'ready');
  const resolvingCount = pendingLines.filter((line) => !line.resolved).length;
  const failMarked = readyLines.filter((line) => line.result === 'fail');
  const passedLines = lines.filter((line) => line.outcome?.status === 'passed');
  const failedInspectionLines = lines.filter((line) => line.outcome?.status === 'failed_inspection');
  const errorLines = lines.filter((line) => line.outcome?.status === 'error');

  const signBlockReason = batchInspectSignBlockReason({
    detailsReady,
    signerName,
    requiresPin,
    pin,
    readyCount: readyLines.length,
    resolvingCount,
  });
  // Shown only after a blocked click; it disappears once the cause is fixed.
  const signError = (signAttempted ? signBlockReason : null) ?? signServerError;

  const signBatch = async (targetLines: InspectLine[]) => {
    // The sign-off name (and PIN) is only needed here, at the final step;
    // lines can be added and marked Pass/Fail without it.
    const name = signerName.trim();
    if (!name || (requiresPin && !pin)) {
      setSignAttempted(true);
      return;
    }
    if (targetLines.length === 0) return;
    setSignAttempted(false);
    setSignServerError(null);
    setIsSigning(true);
    let passed = 0;
    let failedInspections = 0;
    let errors = 0;
    let authFailed = false;

    await runInChunks(targetLines, BATCH_CHUNK_SIZE, async (chunk) => {
      if (authFailed) return null;
      const response = await batchInspect.mutateAsync({
        items: chunk.map((line) => ({
          id: line.resolved!.asset!.id,
          result: line.result,
          notes: line.result === 'fail' ? line.failNotes.trim() : '',
        })),
        inspection_type: 'routine',
        date: inspectionDate,
        notes: notes.trim(),
        signer_name: name,
        ...(pin ? { pin } : {}),
        batch_id: batchId,
        batch_name: batchName.trim(),
        entry_method: 'mixed',
      });
      const byAssetId = new Map(response.results.map((result) => [result.id, result]));
      setLines((current) => current.map((line) => {
        if (!chunk.some((sent) => sent.id === line.id)) return line;
        const result = byAssetId.get(line.resolved!.asset!.id);
        if (!result) {
          return {
            ...line,
            outcome: {
              status: 'error',
              label: 'Failed',
              message: 'The server did not return a result for this serial.',
              tone: 'error',
            },
          };
        }
        return { ...line, outcome: inspectOutcome(result) };
      }));
      for (const result of response.results) {
        if (result.status !== 'created') errors += 1;
        else if (result.result === 'fail') failedInspections += 1;
        else passed += 1;
      }
      return response;
    }, {
      onProgress: setProgress,
      onChunkError: (error, chunk) => {
        const message = errorMessage(error);
        const httpStatus = isAxiosError(error) ? error.response?.status : undefined;
        if (httpStatus === 401) {
          // Wrong / missing PIN: nothing was recorded; stop and let the user fix it.
          authFailed = true;
          setSignServerError(message);
          toast.error(message);
          return;
        }
        if (httpStatus === 403) {
          // Not Inspection Certified: retrying can never succeed, so stop and
          // say what to change instead of offering a retry.
          authFailed = true;
          const certifiedMessage =
            'You are not Inspection Certified, so inspections cannot be signed off. ' +
            'An admin can enable Inspection Certified on your tech profile.';
          setSignServerError(certifiedMessage);
          toast.error(certifiedMessage);
          return;
        }
        errors += chunk.length;
        setLines((current) => current.map((line) => (
          chunk.some((sent) => sent.id === line.id)
            ? { ...line, outcome: { status: 'error', label: 'Failed', message, tone: 'error' } }
            : line
        )));
      },
    });

    setIsSigning(false);
    if (authFailed) {
      playFeedback('error');
      return;
    }
    setPin('');
    setIsFinished(true);
    if (errors > 0) {
      playFeedback('error');
      toast.error(`${passed + failedInspections} recorded · ${errors} could not be saved (retry available).`);
    } else if (failedInspections > 0) {
      playFeedback('duplicate');
      toast.warning(`${passed} passed · ${failedInspections} failed and quarantined. Review the quarantine list.`);
    } else {
      playFeedback('success');
      toast.success(`${passed} inspection${passed === 1 ? '' : 's'} recorded and signed.`);
    }
  };

  const retryErrors = () => {
    const retryLines = errorLines.map((line) => ({ ...line, outcome: undefined }));
    setLines((current) => current.map((line) => (
      line.outcome?.status === 'error' ? { ...line, outcome: undefined } : line
    )));
    setIsFinished(false);
    void signBatch(retryLines);
  };

  const downloadSummary = () => {
    downloadCsv(
      `${slugifyFilename(batchName, 'batch-inspect')}-summary.csv`,
      buildBatchInspectCsv(batchId, batchName, lines)
    );
  };

  const startNewBatch = () => {
    setBatchId(crypto.randomUUID());
    setBatchName(defaultBatchName());
    setNotes('');
    setPin('');
    setSignAttempted(false);
    setSignServerError(null);
    setLines([]);
    setProgress({ processed: 0, total: 0 });
    setIsFinished(false);
    window.setTimeout(() => singleInputRef.current?.focus());
  };

  const renderResultToggle = (line: BatchLine) => {
    const inspectLine = line as InspectLine;
    if (inspectLine.resolved?.status !== 'ready') return null;
    const failed = inspectLine.result === 'fail';
    return (
      <div className="mt-2 space-y-2">
        <div className="inline-flex rounded-md border overflow-hidden" role="group" aria-label={`Result for ${line.serial}`}>
          <button
            type="button"
            className={`px-3 py-1 text-xs font-medium ${!failed ? 'bg-green-600 text-white' : 'bg-white text-gray-700 hover:bg-gray-50'}`}
            aria-pressed={!failed}
            disabled={isSigning}
            onClick={() => updateLine(line.id, { result: 'pass' })}
          >
            Pass
          </button>
          <button
            type="button"
            className={`px-3 py-1 text-xs font-medium border-l ${failed ? 'bg-red-600 text-white' : 'bg-white text-gray-700 hover:bg-gray-50'}`}
            aria-pressed={failed}
            disabled={isSigning}
            onClick={() => updateLine(line.id, { result: 'fail' })}
            data-testid={`fail-${line.serial}`}
          >
            Fail
          </button>
        </div>
        {failed && (
          <Input
            className="h-8"
            placeholder="Reason for failure (e.g. sheath cut at 12 m)"
            value={inspectLine.failNotes}
            disabled={isSigning}
            onChange={(event) => updateLine(line.id, { failNotes: event.target.value })}
            aria-label={`Failure reason for ${line.serial}`}
          />
        )}
      </div>
    );
  };

  return (
    <div id="batch-inspect-fields" className="p-4 sm:p-6 lg:p-8 space-y-6 max-w-7xl mx-auto">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate(-1)} aria-label="Back">
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Batch inspect</h1>
            <p className="text-gray-600 mt-1">
              Scan, paste, or generate serials; every line passes unless you mark it failed. Sign the batch once.
            </p>
          </div>
        </div>
        <Button
          type="button"
          variant="outline"
          onClick={toggleSound}
          aria-label={soundEnabled ? 'Mute scan sounds' : 'Enable scan sounds'}
        >
          {soundEnabled ? <Volume2 className="h-4 w-4 mr-2" /> : <VolumeX className="h-4 w-4 mr-2" />}
          {soundEnabled ? 'Sound on' : 'Sound off'}
        </Button>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>1. Inspection details</CardTitle>
          <CardDescription>Applied to every inspection record in this batch.</CardDescription>
        </CardHeader>
        <CardContent id="batch-inspect-details">
          <fieldset disabled={isSigning} className="grid gap-4 md:grid-cols-3">
            <div className="space-y-2">
              <Label htmlFor="batch-name">Batch name *</Label>
              <Input id="batch-name" value={batchName} onChange={(event) => setBatchName(event.target.value)} required />
            </div>
            <div className="space-y-2">
              <Label htmlFor="inspection-date">Inspection date *</Label>
              <Input
                id="inspection-date"
                type="date"
                max={todayInputValue()}
                value={inspectionDate}
                onChange={(event) => setInspectionDate(event.target.value)}
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="batch-notes">Notes for every record (optional)</Label>
              <Textarea id="batch-notes" rows={1} value={notes} onChange={(event) => setNotes(event.target.value)} />
            </div>
          </fieldset>
        </CardContent>
      </Card>

      {!isFinished && (
        <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_minmax(360px,1.2fr)]">
          <div className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>2. Add gear</CardTitle>
                <CardDescription>All three methods feed the one list on the right.</CardDescription>
              </CardHeader>
              <CardContent>
                <SerialEntryPanel
                  key={batchId}
                  singleInputRef={singleInputRef}
                  ready={detailsReady}
                  disabledPlaceholder="Enter the inspection details first"
                  processing={isSigning}
                  progress={progress}
                  progressLabel="Recording"
                  actionVerb="Add"
                  rangeSummarySuffix="will be added to the list"
                  validationScope="batch-inspect-details"
                  onSingle={(serial) => void handleSingle(serial)}
                  onBatch={handleBatch}
                />
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>3. Sign the batch</CardTitle>
                <CardDescription>
                  One sign-off by the competent person; each asset still gets its own signed record.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="signer-name">Your full name *</Label>
                  <Input
                    id="signer-name"
                    placeholder="Type your full name"
                    value={signerName}
                    onChange={(event) => setSignerName(event.target.value)}
                    disabled={isSigning}
                  />
                </div>
                {requiresPin && (
                  <div className="space-y-2">
                    <Label htmlFor="signer-pin">Signing PIN *</Label>
                    <Input
                      id="signer-pin"
                      type="password"
                      inputMode="numeric"
                      maxLength={6}
                      placeholder="6-digit PIN"
                      value={pin}
                      onChange={(event) => {
                        setPin(event.target.value);
                        setSignServerError(null);
                      }}
                      disabled={isSigning}
                    />
                  </div>
                )}
                {failMarked.length > 0 && (
                  <div className="flex items-start gap-2 rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900">
                    <ShieldAlert className="h-4 w-4 mt-0.5 shrink-0" />
                    <span>
                      {failMarked.length} item{failMarked.length === 1 ? '' : 's'} marked failed will be quarantined when
                      you sign. The rest of the batch still records.
                    </span>
                  </div>
                )}
                <Button
                  type="button"
                  className="w-full"
                  data-skip-required-validation="true"
                  aria-describedby={signError ? 'batch-inspect-sign-error' : undefined}
                  disabled={isSigning}
                  onClick={() => {
                    if (signBlockReason) {
                      setSignAttempted(true);
                      return;
                    }
                    void signBatch(readyLines);
                  }}
                >
                  <PenTool className="h-4 w-4 mr-2" />
                  {isSigning
                    ? 'Signing…'
                    : `Sign & record ${readyLines.length.toLocaleString()} inspection${readyLines.length === 1 ? '' : 's'}`}
                </Button>
                {signError && (
                  <p id="batch-inspect-sign-error" role="alert" className="text-sm font-medium text-red-600">
                    {signError}
                  </p>
                )}
              </CardContent>
            </Card>
          </div>

          <Card className={`transition-colors ${feedbackClasses}`} aria-live="polite">
            <CardHeader>
              <CardTitle>{readyLines.length.toLocaleString()} to inspect</CardTitle>
              <CardDescription>
                {failMarked.length} marked failed · {unmatchedLines.length} unmatched
                {resolvingCount > 0 ? ` · ${resolvingCount} looking up` : ''}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              {unmatchedLines.length > 0 && (
                <div role="alert" className="rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900 flex items-start gap-2">
                  <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
                  <span>
                    {unmatchedLines.length} line{unmatchedLines.length === 1 ? '' : 's'} could not be matched and will be
                    listed in the summary. The rest still record.
                  </span>
                </div>
              )}
              <BatchLineList
                lines={pendingLines}
                sortKey={sortKey}
                onSortKeyChange={setSortKey}
                groupByCategoryEnabled={groupByCategoryEnabled}
                onGroupByCategoryChange={setGroupByCategoryEnabled}
                onRemove={(line) => setLines((current) => current.filter((candidate) => candidate.id !== line.id))}
                renderLineExtra={renderResultToggle}
                disabled={isSigning}
              />
              {lines.some((line) => line.outcome) && pendingLines.length === 0 && (
                <Button type="button" variant="outline" className="w-full" onClick={() => setIsFinished(true)}>
                  View batch summary
                </Button>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      {isFinished && (
        <Card>
          <CardHeader>
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <CardTitle>4. Batch summary</CardTitle>
                <CardDescription>{batchName} · signed by {signerName.trim()}</CardDescription>
              </div>
              <Button type="button" variant="outline" onClick={downloadSummary}>
                <Download className="h-4 w-4 mr-2" />
                Download CSV
              </Button>
            </div>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="grid gap-4 sm:grid-cols-3">
              <div className="rounded-lg border border-green-200 bg-green-50 p-4">
                <CheckCircle2 className="h-5 w-5 text-green-700 mb-2" />
                <p className="text-2xl font-bold text-green-900">{passedLines.length}</p>
                <p className="text-sm text-green-700">Passed &amp; signed</p>
              </div>
              <div className="rounded-lg border border-amber-200 bg-amber-50 p-4">
                <ShieldAlert className="h-5 w-5 text-amber-700 mb-2" />
                <p className="text-2xl font-bold text-amber-900">{failedInspectionLines.length}</p>
                <p className="text-sm text-amber-700">Failed &amp; quarantined</p>
              </div>
              <div className="rounded-lg border border-red-200 bg-red-50 p-4">
                <AlertCircle className="h-5 w-5 text-red-700 mb-2" />
                <p className="text-2xl font-bold text-red-900">{errorLines.length + unmatchedLines.length}</p>
                <p className="text-sm text-red-700">Not recorded</p>
              </div>
            </div>

            {failedInspectionLines.length > 0 && (
              <div role="alert" className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900">
                <span>
                  {failedInspectionLines.length} failed item{failedInspectionLines.length === 1 ? '' : 's'} moved to
                  quarantine. Review them and arrange repair or retirement.
                </span>
                <Button type="button" variant="outline" size="sm" asChild>
                  <Link to="/assets?status=quarantined">Open quarantine list</Link>
                </Button>
              </div>
            )}
            {errorLines.length > 0 && (
              <div role="alert" className="flex items-center justify-between gap-3 rounded-lg border border-red-300 bg-red-50 p-3 text-sm text-red-900">
                <span>{errorLines.length} inspection{errorLines.length === 1 ? '' : 's'} could not be saved.</span>
                <Button type="button" variant="outline" size="sm" onClick={retryErrors} disabled={isSigning}>
                  <RotateCcw className="h-4 w-4 mr-1" />
                  Retry
                </Button>
              </div>
            )}

            <BatchLineList
              lines={lines}
              sortKey={sortKey}
              onSortKeyChange={setSortKey}
              groupByCategoryEnabled={groupByCategoryEnabled}
              onGroupByCategoryChange={setGroupByCategoryEnabled}
              maxHeightClass="max-h-[40rem]"
            />

            <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-between">
              <div className="flex gap-2">
                <Button type="button" variant="outline" asChild>
                  <Link to="/inspections">Inspections</Link>
                </Button>
                <Button type="button" variant="outline" asChild>
                  <Link to="/assets">Assets</Link>
                </Button>
              </div>
              <div className="flex gap-2">
                <Button type="button" variant="outline" onClick={() => setIsFinished(false)}>
                  Continue this batch
                </Button>
                <Button type="button" onClick={startNewBatch}>Start new batch</Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
